import React, { useState } from 'react';

import { TextField } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import { IRouterClient } from '../../../../interfaces/IRouterClient';
import { logInfo } from '../../../../lib/logging';
import { Button as ConfirmButton, FlexContainer, Label } from '../../styled';
import { ChatErrorMessage } from '../ChatError/ChatErrorStyles';
import { ExitButtonContainer } from '../ChatExit/ChatExitStyles';
import SuggestedActionsStyle from '../SuggestedActions/SuggestedActionsStyles';

interface IClientRouterModalProps {
  setChatKey: (chatKey: string) => void;
  startChat: () => void;
  chatKey?: string;
  clientRouterKey: string;
}

const selectedServiceErrorMessage = 'The selected service is not available for your zipcode.';
const noZipFoundErrorMessage = 'No service is available for your zipcode';

const ClientRouterModal = (props: IClientRouterModalProps) => {
  const [zipcode, setZipcode] = useState('');
  const [selectedService, setSelectedService] = useState<string | undefined>(undefined);
  const [branchesInZip, setBranchesInZip] = useState<IRouterClient[]>([]);
  const [zipcodeError, setZipcodeError] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [clientRouterData, setClientRouterData] = useState<IRouterClient[]>([]);

  const { startChat, setChatKey, chatKey, clientRouterKey } = props;

  const serviceOptionsObj: { [key: string]: string } = {};

  // TODO: Might be easier to test if this happened in a parent container
  const script = document.createElement('script');
  script.async = true;
  script.src = `${process.env.REACT_APP_ROUTER_URL}${clientRouterKey}/se_inc_def.js`;
  script.onload = () => {
    setClientRouterData((window as any).se_client_def_var as IRouterClient[]);
  };
  document.body.appendChild(script);

  // compile list of services
  clientRouterData.length &&
    clientRouterData.forEach((client) => {
      client.services.forEach((service) => {
        if (serviceOptionsObj[service] === undefined) {
          serviceOptionsObj[service] = service;
        }
      });
    });

  const serviceOptions = Object.keys(serviceOptionsObj);

  const checkServiceForBranches = (branches: IRouterClient[], service: string) => {
    if (branches.length === 0 || service === undefined) {
      return;
    }

    const branchWithService = branches.find((branch) => branch.services.includes(service));

    if (branchWithService) {
      const env = process.env.REACT_APP_AWS_ACCOUNT_KEY;
      if (env === 'production') {
        setChatKey(branchWithService.chat_key);
      } else {
        // set chat key to haller's chat key for non-prod envs
        logInfo(`Client Router Client: ${branchWithService.client_key}`);
        logInfo(`Client Router Key: ${branchWithService.chat_key}`);
        setChatKey('kk7hc5s86hf0d8sng84k1ao5pgnd6s');
      }
    } else {
      setServiceError(selectedServiceErrorMessage);
      setChatKey('');
    }
  };

  const lookupZip = (zip: string) => {
    const newBranchesInZip = clientRouterData.filter((client) => client.service_area.includes(zip));
    if (newBranchesInZip.length !== 0) {
      setBranchesInZip(newBranchesInZip);

      if (selectedService) {
        checkServiceForBranches(newBranchesInZip, selectedService);
      }
    } else {
      // no zip found
      setZipcodeError(noZipFoundErrorMessage);
    }
  };

  const onZipcodeChange = (newZip: string) => {
    clearErrors();
    if (branchesInZip.length > 0) {
      // clear branches
      setBranchesInZip([]);
    }
    if (newZip.length === 5) {
      lookupZip(newZip);
    }
    setZipcode(newZip);
  };

  const onServiceChange = (newSelectedService: string) => {
    clearErrors();
    setSelectedService(newSelectedService);
    if (branchesInZip.length !== 0) {
      checkServiceForBranches(branchesInZip, newSelectedService);
    }
  };

  const clearErrors = () => {
    setServiceError('');
    setZipcodeError('');
  };

  return (
    <ExitButtonContainer>
      <Label>Please enter your zipcode</Label>
      <br />
      <TextField
        required
        value={zipcode}
        id='outlined-required'
        label='Zipcode'
        variant='outlined'
        onChange={(e) => onZipcodeChange(e.target.value)}
        placeholder='Enter your zipcode'
      />
      <ChatErrorMessage noBackground>{zipcodeError}</ChatErrorMessage>
      <br />
      <Label>Select your service</Label>
      <SuggestedActionsStyle>
        {serviceOptions.map((service) => (
          <Button
            onClick={() => onServiceChange(service)}
            key={service}
            selected={selectedService === service}
            look='outline'
          >
            {service}
          </Button>
        ))}
      </SuggestedActionsStyle>
      <ChatErrorMessage noBackground>{serviceError}</ChatErrorMessage>
      <FlexContainer spaceBetween>
        <ConfirmButton disabled={!chatKey} onClick={startChat} filled>
          Get Started
        </ConfirmButton>
      </FlexContainer>
    </ExitButtonContainer>
  );
};

export default ClientRouterModal;
